export type RegionBucket = "EU" | "UK" | "US_OPT_OUT" | "OTHER" | "UNKNOWN";

export type RegionInfo = {
  countryCode?: string;
  regionBucket: RegionBucket;
  requiresConsent: boolean;
  updatedAt: number;
};

export type RegionHealthStatus = {
  lastSuccessAt?: number;
  lastFailureAt?: number;
  consecutiveFailures: number;
  lastFailureReason?: string;
};

const REGION_STORAGE_KEY = "kfa_region_info";
const REGION_HEALTH_STORAGE_KEY = "kfa_region_health_v1";
const REGION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const FALLBACK_REGION_INFO: RegionInfo = {
  regionBucket: "EU",
  requiresConsent: true,
  updatedAt: 0,
};
let inFlightRegionInfoPromise: Promise<RegionInfo> | null = null;

function readStoredRegionInfo(): RegionInfo | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(REGION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RegionInfo;
    if (!parsed?.updatedAt) return null;
    if (Date.now() - parsed.updatedAt > REGION_TTL_MS) {
      window.localStorage.removeItem(REGION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn("Failed to read region info:", error);
    return null;
  }
}

function storeRegionInfo(info: RegionInfo): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(REGION_STORAGE_KEY, JSON.stringify(info));
  } catch (error) {
    console.warn("Failed to store region info:", error);
  }
}

function readRegionHealthStatus(): RegionHealthStatus {
  if (typeof window === "undefined") {
    return { consecutiveFailures: 0 };
  }
  try {
    const raw = window.localStorage.getItem(REGION_HEALTH_STORAGE_KEY);
    if (!raw) {
      return { consecutiveFailures: 0 };
    }
    const parsed = JSON.parse(raw) as RegionHealthStatus;
    return {
      consecutiveFailures:
        typeof parsed?.consecutiveFailures === "number"
          ? parsed.consecutiveFailures
          : 0,
      lastSuccessAt:
        typeof parsed?.lastSuccessAt === "number"
          ? parsed.lastSuccessAt
          : undefined,
      lastFailureAt:
        typeof parsed?.lastFailureAt === "number"
          ? parsed.lastFailureAt
          : undefined,
      lastFailureReason:
        typeof parsed?.lastFailureReason === "string"
          ? parsed.lastFailureReason
          : undefined,
    };
  } catch (error) {
    console.warn("Failed to read region health status:", error);
    return { consecutiveFailures: 0 };
  }
}

function storeRegionHealthStatus(status: RegionHealthStatus): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(REGION_HEALTH_STORAGE_KEY, JSON.stringify(status));
  } catch (error) {
    console.warn("Failed to store region health status:", error);
  }
}

function markRegionFetchSuccess(): void {
  const previous = readRegionHealthStatus();
  storeRegionHealthStatus({
    consecutiveFailures: 0,
    lastSuccessAt: Date.now(),
    lastFailureAt: previous.lastFailureAt,
    lastFailureReason: previous.lastFailureReason,
  });
}

function markRegionFetchFailure(reason: string): void {
  const previous = readRegionHealthStatus();
  storeRegionHealthStatus({
    consecutiveFailures: previous.consecutiveFailures + 1,
    lastSuccessAt: previous.lastSuccessAt,
    lastFailureAt: Date.now(),
    lastFailureReason: reason,
  });
}

export function getStoredRegionInfo(): RegionInfo | null {
  return readStoredRegionInfo();
}

export function getStoredRegionHealthStatus(): RegionHealthStatus {
  return readRegionHealthStatus();
}

/**
 * How long the region fetch may take before we fall back to the conservative
 * default. Everything that awaits `ensureRegionInfo` - the paywall product
 * load, the consent banner - would otherwise hang as long as this request
 * does, and in-app browsers (Instagram, Facebook) are known to leave requests
 * hanging instead of failing them.
 */
const REGION_FETCH_TIMEOUT_MS = 8000;

export async function fetchRegionInfo(): Promise<RegionInfo> {
  try {
    const response = await fetch("/api/region", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      ...(typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
        ? { signal: AbortSignal.timeout(REGION_FETCH_TIMEOUT_MS) }
        : {}),
    });
    if (!response.ok) {
      throw new Error(`Region fetch failed: ${response.status}`);
    }
    const data = (await response.json()) as {
      countryCode?: string;
      regionBucket?: RegionBucket;
      requiresConsent?: boolean;
    };
    const info: RegionInfo = {
      countryCode: data.countryCode,
      regionBucket: data.regionBucket ?? "UNKNOWN",
      requiresConsent: data.requiresConsent ?? true,
      updatedAt: Date.now(),
    };
    storeRegionInfo(info);
    markRegionFetchSuccess();
    return info;
  } catch (error) {
    const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    console.warn(`Failed to fetch region info: ${message}`);
    markRegionFetchFailure(message);
    const fallback: RegionInfo = {
      ...FALLBACK_REGION_INFO,
      updatedAt: Date.now(),
    };
    const health = getStoredRegionHealthStatus();
    console.info("[region] using conservative fallback bucket", {
      fallbackRegionBucket: fallback.regionBucket,
      consecutiveFailures: health.consecutiveFailures,
      lastFailureAt: health.lastFailureAt,
    });
    storeRegionInfo(fallback);
    return fallback;
  }
}

export async function ensureRegionInfo(): Promise<RegionInfo> {
  const stored = readStoredRegionInfo();
  if (stored) {
    return stored;
  }

  if (inFlightRegionInfoPromise) {
    return inFlightRegionInfoPromise;
  }

  inFlightRegionInfoPromise = fetchRegionInfo().finally(() => {
    inFlightRegionInfoPromise = null;
  });

  return inFlightRegionInfoPromise;
}
